<template>
  <form
    class="w-full max-w-md flex flex-col bg-white gap-4 p-6 rounded-2xl border border-slate-200 m-6 shadow-xl shadow-primary/2"
    @submit="onSubmit"
  >
    <label class="text-center mb-4">
      <img
        src="/AppIcon.svg"
        alt="logo"
        class="w-20 h-20 mx-auto mb-2"
      >
      <h1 class="text-2xl font-bold text-foreground/80">
        注册
      </h1>
    </label>

    <FormField
      v-slot="{ componentField }"
      name="username"
    >
      <FormItem>
        <FormLabel>用户名</FormLabel>
        <FormControl>
          <Input
            type="text"
            placeholder="请输入用户名"
            v-bind="componentField"
            autocomplete="username"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-slot="{ componentField }"
      name="phone"
    >
      <FormItem>
        <FormLabel>手机号</FormLabel>
        <FormControl>
          <Input
            type="tel"
            placeholder="请输入手机号"
            v-bind="componentField"
            autocomplete="tel"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-slot="{ componentField }"
      name="password"
    >
      <FormItem>
        <FormLabel>密码</FormLabel>
        <FormControl>
          <Input
            type="password"
            placeholder="请输入密码"
            v-bind="componentField"
            autocomplete="new-password"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-slot="{ componentField }"
      name="confirmPassword"
    >
      <FormItem>
        <FormLabel>确认密码</FormLabel>
        <FormControl>
          <Input
            type="password"
            placeholder="请再次输入密码"
            v-bind="componentField"
            autocomplete="new-password"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <Button
      type="submit"
      :disabled="pending"
      class="w-full mt-2"
    >
      <Icon
        v-if="pending"
        name="lucide:loader-2"
        class="mr-2 h-4 w-4 animate-spin"
      />
      {{ pending ? '注册中...' : '注册' }}
    </Button>

    <div class="text-center mt-4">
      <p class="text-sm text-slate-500">
        已经有账号？
        <NuxtLink
          to="/login"
          class="text-blue-600 hover:text-blue-800 font-medium"
        >
          立即登录
        </NuxtLink>
      </p>
    </div>
  </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import * as z from 'zod'
import { FetchError } from 'ofetch'
import { toast } from 'vue-sonner'

const { register } = useAuth()
const router = useRouter()
const route = useRoute()

definePageMeta({
  layout: 'centered',
})

// 获取重定向路径
const redirectPath = computed(() => {
  const redirect = route.query.redirect as string
  return redirect ? decodeURIComponent(redirect) : '/'
})

// 表单验证模式
const formSchema = toTypedSchema(z.object({
  username: z.string()
    .min(2, '用户名至少2位')
    .max(20, '用户名不能超过20位'),
  phone: z.string()
    .min(11, '手机号必须为11位')
    .max(11, '手机号必须为11位')
    .regex(/^1[3-9]\d{9}$/, '请输入正确的手机号'),
  password: z.string()
    .min(6, '密码至少6位')
    .max(50, '密码不能超过50位'),
  confirmPassword: z.string()
    .min(6, '确认密码至少6位')
    .max(50, '确认密码不能超过50位'),
}).refine(data => data.password === data.confirmPassword, {
  message: '两次输入的密码不一致',
  path: ['confirmPassword'],
}))

const form = useForm({
  validationSchema: formSchema,
  initialValues: {
    username: '',
    phone: '',
    password: '',
    confirmPassword: '',
  },
})

const onSubmit = form.handleSubmit(async (values) => {
  try {
    pending.value = true
    await register({
      username: values.username,
      phone: values.phone,
      password: values.password,
    })

    await router.push(redirectPath.value)
  }
  catch (error) {
    if (error instanceof FetchError) {
      if (error.status === 409) {
        toast.error('手机号已被注册')
      }
      else {
        toast.error('注册失败')
        console.error(error)
      }
    }
  }
  finally {
    pending.value = false
  }
})

const pending = ref(false)
</script>
